import React, { useState } from 'react';
import styled from 'styled-components';
import {
  Button,
  Heading,
  Modal,
  Option,
  Select,
} from '@alefeducation/ui-elements-library-core';
import { Times } from '@alefeducation/ui-elements-library-icons';
import CsvOperationProgressBar, {
  CsvOperationProgress,
} from './CsvOperationProgressBar';
import { Organisation } from '../../../apollo/queries';
import UploadFiles from '../../UploadFiles/UploadFiles';
import FormControlContainer from '../CreateNewRole/FormControlContainer';
import { IMPORT_CUSTOM_ROLES_TITLE } from '../../../utils/constants';

type Props = {
  organisations: Pick<Organisation, 'name' | 'code'>[];
  isImporting: boolean;
  importProgress: CsvOperationProgress | null;
  onClose: () => void;
  onDownloadTemplate: () => void;
  onImport: (
    file: File,
    organisation: Pick<Organisation, 'name' | 'code'>,
  ) => void;
};

const ImportCustomRolesModal = ({
  organisations,
  isImporting,
  importProgress,
  onClose,
  onDownloadTemplate,
  onImport,
}: Props) => {
  const [selectedOrganisation, setSelectedOrganisation] = useState<
    Pick<Organisation, 'name' | 'code'>
  >({ name: '', code: '' });
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const canImport =
    selectedOrganisation.code !== '' &&
    uploadedFile?.type === 'text/csv' &&
    !isImporting;

  const handleImport = () => {
    if (uploadedFile && canImport) {
      onImport(uploadedFile, selectedOrganisation);
    }
  };

  return (
    <Modal open size="medium">
      <ImportModalContainer>
        <CloseIcon $disabled={isImporting}>
          <Times
            className="import-custom-roles-modal__close-icon"
            color="primary.80"
            data-testid="close-import-modal"
            height={14}
            width={14}
            onClick={isImporting ? undefined : onClose}
          />
        </CloseIcon>
        <ImportModalContent>
          <Heading color="text100" variant="heading-small" weight="bold">
            {IMPORT_CUSTOM_ROLES_TITLE}
          </Heading>
          <FormControlContainer title="Select Organization *">
            <Select
              onSelectChange={() => {}}
              selectedOption={selectedOrganisation.name}
              data-testid="import-select-organisation"
              placeholder=""
              searchable
            >
              {organisations.map((org) => (
                <Option
                  onClick={() => setSelectedOrganisation(org)}
                  key={org.code}
                >
                  {org.name}
                </Option>
              ))}
            </Select>
          </FormControlContainer>
          <UploadFiles
            onFilesUploaded={setUploadedFile}
            uploadedFile={uploadedFile}
          />
          {!uploadedFile && (
            <DownloadTemplate
              data-testid="download-template-link"
              onClick={onDownloadTemplate}
            >
              Download our Template
            </DownloadTemplate>
          )}
          {importProgress && (
            <CsvOperationProgressBar
              data-testid="import-custom-roles-progress"
              progress={importProgress}
            />
          )}
        </ImportModalContent>
        <ImportModalActions>
          <Button
            variant="secondary"
            size="sm"
            data-testid="bt-import-cancel"
            onClick={onClose}
            disabled={isImporting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            data-testid="bt-import-custom-roles"
            onClick={handleImport}
            disabled={!canImport}
          >
            Import
          </Button>
        </ImportModalActions>
      </ImportModalContainer>
    </Modal>
  );
};

export default ImportCustomRolesModal;

const ImportModalContainer = styled.div`
  padding: 16px 0 40px 0;
`;

const CloseIcon = styled.div<{ $disabled?: boolean }>`
  height: 14px;
  position: absolute;
  right: 15px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
`;

const ImportModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  border-bottom: 1px solid $break-line;
  padding: 24px 40px;
`;

const ImportModalActions = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  padding: 24px 40px 0;

  button {
    width: 146px;
    height: 40px;
  }
`;

const DownloadTemplate = styled.button`
  background: none;
  border: none;
  color: ${({ theme }: any) => theme.colors.interactive[100]};
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  margin-top: 8px;
  padding: 0;
  text-decoration: underline;
`;
